import type { ReactNode } from 'react';
import { useState } from 'react';
import { Button, ConfirmDialog } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useIvrMapVersions } from '../hooks/useIvrMapVersions';
import { useActivateMapVersion } from '../hooks/useActivateMapVersion';
import { useMapVersionDetail } from '../hooks/useMapVersionDetail';
import {
  stateKindBadge,
  confirmedActionFrom,
  representativeStatus,
  type StateKindBadge,
} from '../utils/ivrGraphDisplay';
import type { PayerMapVersion, VersionDiffSummary } from '../api/payersApi';
import styles from './IvrMapCard.module.css';

const KIND_CLASS: Record<StateKindBadge, string> = {
  gate: 'kindGate',
  reached: 'kindReached',
  unavailable: 'kindUnavailable',
  announcement: 'kindAnnouncement',
  entry: 'kindEntry',
  menu: 'kindMenu',
  terminal: 'kindTerminal',
};

function statusLabel(status: PayerMapVersion['status']): string {
  if (status === 'active') return 'ACTIVE';
  if (status === 'proposed') return 'PROPOSED';
  return 'SUPERSEDED';
}

const STATUS_CLASS = {
  unresolved: 'stateUnresolved',
  unavailable: 'stateUnavailable',
} as const;

// Shared by the charted-states list and the representative-path placeholder
// row below it — same number/prompt/action layout either way, just with or
// without a real state behind it.
function StateRow({
  number,
  prompt,
  badge,
  action,
  status,
}: {
  number: string;
  prompt: string;
  badge?: { label: string; variant: StateKindBadge };
  action: ReactNode;
  status?: 'unresolved' | 'unavailable';
}) {
  return (
    <div className={status ? `${styles.state} ${styles[STATUS_CLASS[status]]}` : styles.state}>
      <span className={styles.stateNumber}>{number}</span>
      <div className={styles.stateBody}>
        <div className={styles.prompt}>
          {prompt}
          {badge && (
            <span className={`${styles.kindBadge} ${styles[KIND_CLASS[badge.variant]]}`}>
              {badge.label}
            </span>
          )}
        </div>
        <div className={styles.action}>{action}</div>
      </div>
    </div>
  );
}

// The hero card — every version is browsable here (not just the active
// one), so a proposed version's evidence can be inspected before deciding
// whether to activate it. Two panes: the charted states on the left, the
// rendered instructions an agent actually follows on the right.
export function IvrMapCard({ payerId }: { payerId: string }) {
  const { bundle, isLoading } = useIvrMapVersions(payerId);
  const activate = useActivateMapVersion(payerId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmVersionId, setConfirmVersionId] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const versions = bundle?.versions ?? [];
  const effectiveSelectedId = selectedId ?? bundle?.active?.id ?? versions[0]?.id ?? null;
  const { version: detail } = useMapVersionDetail(effectiveSelectedId);
  const repStatus = detail ? representativeStatus(detail.map) : 'unresolved';

  if (isLoading) return null;

  function selectVersion(id: string) {
    setSelectedId(id);
    setShowDiff(false);
    setShowRawJson(false);
  }

  function handleCopy() {
    if (!detail) return;
    navigator.clipboard
      .writeText(detail.renderedText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err: unknown) => console.warn('Clipboard write failed', err));
  }

  return (
    <div>
      <div className={styles.titleRow}>
        <div className={styles.titleLeft}>
          <span className={styles.titleLabel}>IVR map</span>
          {versions.length > 0 && (
            <div className={styles.versionPills}>
              {versions.map((v) => (
                <span
                  key={v.id}
                  className={
                    v.id === effectiveSelectedId
                      ? `${styles.versionPill} ${styles.versionPillSelected}`
                      : styles.versionPill
                  }
                  onClick={() => selectVersion(v.id)}
                >
                  v{v.version} {statusLabel(v.status)}
                </span>
              ))}
            </div>
          )}
          {detail && (
            <span className={styles.hint}>
              synthesized {new Date(detail.createdAt).toLocaleDateString()} from{' '}
              {detail.sourceRunIds.length} run{detail.sourceRunIds.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className={styles.titleActions}>
          {detail?.diffSummary && (
            <Button size="sm" variant="secondary" onClick={() => setShowDiff((s) => !s)}>
              {showDiff ? 'Hide diff' : 'View diff'}
            </Button>
          )}
          {detail?.status === 'proposed' && (
            <Button size="sm" onClick={() => setConfirmVersionId(detail.id)}>
              Activate v{detail.version}
            </Button>
          )}
        </div>
      </div>

      {showDiff && detail && (
        <div className={styles.diffBlock}>{renderDiff(detail.diffSummary)}</div>
      )}

      {!detail ? (
        <div className={patterns.gridTableEmpty}>
          No versions yet — synthesis runs automatically once charting evidence comes in.
        </div>
      ) : (
        <div className={styles.mapGrid}>
          <div className={styles.left}>
            <div className={styles.paneHeader}>
              <span>Charted states</span>
              <span className={styles.paneLink} onClick={() => setShowRawJson((s) => !s)}>
                {showRawJson ? 'hide raw JSON' : 'raw JSON'}
              </span>
            </div>

            {showRawJson ? (
              <pre className={styles.monoBlock}>{JSON.stringify(detail.map, null, 2)}</pre>
            ) : (
              <>
                {detail.map.states.map((state, i) => {
                  const action = confirmedActionFrom(detail.map, state.id);
                  return (
                    <StateRow
                      key={state.id}
                      number={String(i + 1)}
                      prompt={`“${state.prompt}”`}
                      badge={stateKindBadge(state)}
                      action={
                        state.outcome === 'subscriber_data_required' ? (
                          <>
                            → key the patient&apos;s real subscriber/member ID (and date of birth,
                            if asked). This step is patient-specific and isn&apos;t chartable.
                          </>
                        ) : state.outcome === 'representative_unavailable' ? (
                          <>
                            → terminal — this payer confirms no representative exists for this call
                            type.
                          </>
                        ) : action ? (
                          <>
                            → <b>{action}</b>
                          </>
                        ) : (
                          '— call reached this point; no confirmed next step yet'
                        )
                      }
                    />
                  );
                })}
                {repStatus !== 'reached' && (
                  <StateRow
                    number="?"
                    prompt="Path to a representative"
                    action={
                      repStatus === 'unavailable'
                        ? 'confirmed unavailable — this payer states no representative exists for this call type'
                        : 'unresolved — no run has confirmed reaching a human or queue yet'
                    }
                    status={repStatus === 'unavailable' ? 'unavailable' : 'unresolved'}
                  />
                )}
              </>
            )}
          </div>

          <div>
            <div className={styles.paneHeader}>
              <span>Rendered instructions — what the agent follows</span>
              <span className={styles.paneLink} onClick={handleCopy}>
                {copied ? 'copied' : 'copy'}
              </span>
            </div>
            <pre className={styles.monoBlock}>{detail.renderedText}</pre>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmVersionId}
        onClose={() => setConfirmVersionId(null)}
        onConfirm={() => {
          if (confirmVersionId) {
            activate.mutate(confirmVersionId, { onSuccess: () => setConfirmVersionId(null) });
          }
        }}
        title="Activate this version?"
        message="This updates what the agent follows on the next call and regenerates this payer's test suite."
        confirmLabel="Activate"
        isLoading={activate.isPending}
      />
    </div>
  );
}

function renderDiff(diff: VersionDiffSummary | null) {
  if (!diff || (!diff.navigation && !diff.knowledge)) {
    return <span className={styles.diffEmpty}>No changes recorded for this version.</span>;
  }
  const nav = diff.navigation;
  const knowledge = diff.knowledge;
  const nothingChanged =
    (!nav ||
      (nav.statesAdded.length === 0 &&
        nav.transitionsAdded.length === 0 &&
        nav.transitionsChanged.length === 0)) &&
    (!knowledge || knowledge.fieldsChanged.length === 0);

  if (nothingChanged) {
    return <span>No changes vs. the active version.</span>;
  }

  return (
    <>
      {nav && nav.statesAdded.length > 0 && (
        <div>
          <b>States added:</b>
          <ul>
            {nav.statesAdded.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {nav && nav.transitionsAdded.length > 0 && (
        <div>
          <b>Transitions added:</b>
          <ul>
            {nav.transitionsAdded.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {nav && nav.transitionsChanged.length > 0 && (
        <div>
          <b>Transitions changed:</b>
          <ul>
            {nav.transitionsChanged.map((c) => (
              <li key={`${c.description}|${c.before}|${c.after}`}>
                {c.description}: {c.before} → {c.after}
              </li>
            ))}
          </ul>
        </div>
      )}
      {knowledge && knowledge.fieldsChanged.length > 0 && (
        <div>
          <b>Knowledge changed:</b>
          <ul>
            {knowledge.fieldsChanged.map((c) => (
              <li key={c.field}>
                {c.field}: {c.before} → {c.after}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
